import { useEffect, useState } from "react";
import { Bell, BellOff, Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  currentPermission,
  requestNotificationPermission,
  scheduleAppointmentReminders,
  scheduleMedicationReminders,
} from "@/lib/reminders";

export default function ReminderBanner() {
  const [permission, setPermission] = useState(currentPermission());
  const [installPrompt, setInstallPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem("gn_banner_dismissed_at") &&
        Date.now() - Number(localStorage.getItem("gn_banner_dismissed_at")) < 24 * 3600 * 1000;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (permission === "granted") {
      scheduleAppointmentReminders();
      scheduleMedicationReminders();
    }
  }, [permission]);

  const enable = async () => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === "granted") {
      await scheduleAppointmentReminders();
      await scheduleMedicationReminders();
    }
  };

  const installPwa = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const dismiss = () => {
    try {
      localStorage.setItem("gn_banner_dismissed_at", String(Date.now()));
    } catch {}
    setDismissed(true);
  };

  const shouldShow =
    !dismissed && (permission === "default" || installPrompt);

  if (!shouldShow) return null;

  return (
    <div
      className="relative flex flex-col gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
      data-testid="reminder-banner"
    >
      <button
        onClick={dismiss}
        className="absolute right-3 top-3 rounded p-1 text-muted-foreground hover:bg-secondary"
        aria-label="Dismiss"
        data-testid="reminder-banner-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          {permission === "granted" ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
        </div>
        <div>
          <div className="font-heading text-sm font-bold">
            {permission === "default"
              ? "Never miss a dose or appointment"
              : "Install GuardianNest as an app"}
          </div>
          <div className="text-xs text-muted-foreground">
            {permission === "default"
              ? "Get gentle browser notifications for upcoming appointments and scheduled meds."
              : "One-tap access from your phone or desktop — works offline for critical info."}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        {permission === "default" && (
          <Button size="sm" onClick={enable} data-testid="reminder-enable">
            <Bell className="mr-1 h-3.5 w-3.5" /> Turn on
          </Button>
        )}
        {installPrompt && (
          <Button size="sm" variant="outline" onClick={installPwa} data-testid="pwa-install">
            <Download className="mr-1 h-3.5 w-3.5" /> Install app
          </Button>
        )}
      </div>
    </div>
  );
}
